import { downloadCSVFiles, getDateWithTimeString } from '@/helpers/shadcn/utils'
import { useDataTable } from '@/hooks/useDataTable-hook'
import { GetFormattedSalesTargetData } from '@/models/SalesTargetListModel'
import { SalesTargetsColConfig } from '@/services/MockData/SalesTargets/SalesTargetsColConfig'
import { getSalesTargetCsvFile, getSalesTargetsList, addDraftSalesTargetList, updateDraftSalesTargetList, addSalesTargetList,updateEditSalesTargetTemplate, validateSalesTargetName } from '@/services/salesTargets'
import { useQueries } from '@tanstack/react-query'
import { getISOTimeString, getISOTimeStringForReview, getUUID, handleNameValidationViaCallbackFn } from '@/utils/commons'
import { useMemo, useState } from 'react'
import { createSalesTargetRequestObject, tranformRuleSetOfSalesTarget } from '@/pages/SalesTargetUtils'
import { useAppDispatch } from '@/store'
import { resetSalesTarget } from '@/store/slices/salesTargetSlice';
import { setToastNotifications } from '@/store/slices/commonSlice'
import { TARGET_TYPES, IMAGETYPE } from '@/utils/constants/saleTargetConstants'
import { Constants } from '@/utils/constants/commonConstants'

/**
 * Hook for managing sales targets data and actions
 * @param {boolean} enableQuery - Whether to enable the sales targets list query. 
 *                                Set to false for pages that only need the utility functions (handleDraft, handlePublish, etc.)
 *                                and don't need the list data to avoid unnecessary API calls.
 */

const initialSortConfig = {
  column: SalesTargetsColConfig[8].name_of_db_column,
  direction: 'desc',
}

export const useSalesTargets = (enableQuery = true) => {
  const dispatch = useAppDispatch()
  const {setPageIndex, setSortingObj, setSearchArry, pageIndex, sortingObj, searchArr, handleResetAll} = useDataTable(0, false, false, initialSortConfig)
  const [isNameValid, setIsNameValid] = useState(true)

  const handleValidationModalClose = () => {
    setIsNameValid(true)
  }

  // Helper function to show toast notifications
  const showToast = (message, type = 'info') => {
    dispatch(setToastNotifications({show: true, message, type}))
  }

  // Move search params to request body and only use query params for pagination/sorting
  const queryParams = useMemo(() => {
    const params = new URLSearchParams()

    // Add sorting parameters if available
    if (sortingObj?.column) {
      params.append('sortBy', sortingObj.column)
      params.append('order', sortingObj.direction)
    }

    // Add pagination parameters
    params.append('pageSize', 10)
    params.append('page', pageIndex || 0)

    //this code can be used to filter the data based on the searchArr

    // // Add search parameters if available
    // if (searchArr.length > 0) {
    //   searchArr.forEach(searchObj => {
    //     if (searchObj.type === 'date') {
    //       // params.append(searchObj.field, getFormattedDateString(searchObj.value))
    //     } else {
    //       // params.append(searchObj.field, searchObj.value)
    //     }
    //   })
    // }

    return params.toString()
  }, [sortingObj?.column, sortingObj?.direction, pageIndex])


  // Check if date range is complete
  const isValidDateRange = useMemo(() => {
    if (!searchArr?.length) return true;
    // Find any dateRange type search
    const dateRangeSearch = searchArr.find(s => s.type === 'dateRange');
    // If no dateRange type found, return true (valid)
    if (!dateRangeSearch) return true;
    // For dateRange type, validate it has both start and end dates
    const [startDate, endDate] = dateRangeSearch.value.split(',');
    return Boolean(startDate && endDate && !dateRangeSearch.value.endsWith(','));
  }, [searchArr]);

  const [{ data, isLoading, isFetching }] = useQueries({
    queries: [
      {
        queryKey: ['SALES_TARGETS', queryParams, searchArr],
        queryFn: () => getSalesTargetsList(queryParams, searchArr),
        enabled: enableQuery && isValidDateRange // Only call API when explicitly enabled and date range is complete
      },
    ],
  })

  const onDownloadBtnClick = async selectedIds => {
    const body = {
      "ids": selectedIds,
      "filters": searchArr,
    }
    const data = await getSalesTargetCsvFile(queryParams, body)
    const fileName = `SalesTarget_${getDateWithTimeString()}`
    downloadCSVFiles(data, fileName)
  }

  const formatDataForStep = (formData, step) => {
    switch (step) {
      case 'setup':
        return {
          ...formData,
          displayStartDate: getISOTimeString(formData?.activeDisplay ? formData?.startDate : formData?.displayStartDate, false, true),
          displayEndDate: getISOTimeString(formData?.activeDisplay ? formData?.endDate : formData?.displayEndDate, false, true),
          pointsDeliveryDate: getISOTimeString(formData?.pointsDeliveryDate, false, true),
          startDate: getISOTimeString(formData?.startDate, false, true),
          endDate: getISOTimeString(formData?.endDate, false, true),
          activeStartDate: getISOTimeString(formData?.activeStartDate, false, true),
          activeEndDate: getISOTimeString(formData?.activeEndDate, false, true),
          targetDeliveryDate: getISOTimeString(formData?.targetDeliveryDate, false, true),
          targetType: formData?.targetTypeValue || "",
        }
      case 'displayContent':
      case 'products':
        return {
          ...formData,
          displayStartDate: getISOTimeString(formData?.activeDisplay ? formData?.startDate : formData?.displayStartDate, false, true),
          displayEndDate: getISOTimeString(formData?.activeDisplay ? formData?.endDate : formData?.displayEndDate, false, true),
          pointsDeliveryDate: getISOTimeString(formData?.pointsDeliveryDate, false, true),
          startDate: getISOTimeString(formData?.startDate, false, true),
          endDate: getISOTimeString(formData?.endDate, false, true),
          targetDeliveryDate: getISOTimeString(formData?.targetDeliveryDate, false, true),
          activeStartDate: getISOTimeString(formData?.activeStartDate, false, true),
          activeEndDate: getISOTimeString(formData?.activeEndDate, false, true),
        }
      default:
        return formData
    }
  }

  // Helper function to filter display content
  const filterDisplayContent = (displayContent, excludeChild = false) => {
    return Object.keys(displayContent || {})
      .filter(langKey => !excludeChild || langKey !== 'child')
      .reduce((acc, langKey) => {
        acc[langKey] = displayContent?.[langKey];
        return acc;
      }, {});
  };

  // Helper function to format all date fields
  const formatDateFields = (salesTargetData) => ({
    activeStartDate: getISOTimeStringForReview(salesTargetData?.activeStartDate, false, true),
    activeEndDate: getISOTimeStringForReview(salesTargetData?.activeEndDate, false, true),
    targetDeliveryDate: getISOTimeStringForReview(salesTargetData?.targetDeliveryDate, false, true),
    displayStartDate: getISOTimeStringForReview(salesTargetData?.displayStartDate, false, true),
    displayEndDate: getISOTimeStringForReview(salesTargetData?.displayEndDate, false, true),
    pointsDeliveryDate: getISOTimeStringForReview(salesTargetData?.pointsDeliveryDate, false, true)
  });

  // Helper function to transform media array to include id key
  const transformMediaArray = (mediaArray) => {
    return mediaArray.map(mediaItem => {
      let mappedId = mediaItem.imageId || mediaItem.id;
      
      // Map display text to actual values if needed
      if (mappedId === 'Foreground') {
        mappedId = IMAGETYPE.Foreground;
      } else if (mappedId === 'Background') {
        mappedId = IMAGETYPE.Background;
      }
      
      return {
        ...mediaItem,
        id: mappedId
      };
    });
  };

  // Helper function to create metadata object
  const createMetadata = (salesTargetData, tabType) => {
    const mediaFile = salesTargetData?.tabWiseData?.[tabType]?.mediaFile;
    const hasOneFile = mediaFile?.length === 1;
    return {
      productFileName: hasOneFile ? mediaFile[0] : '',
      isProductIdsUploaded: hasOneFile
    };
  };

  // Helper function to create application object
  const createApplicationObject = () => ({
    createdAt: "",
    updatedAt: "",
    createdBy: null,
    updatedBy: null,
    id: "",
    country: "",
    siteId: "",
    timezone: "",
    pointsMultiplierRuleSet: {
      rules: [
        {
          name: "Order Channel",
          matchWith: ["online"],
          multiplier: 2.0
        },
        {
          name: "Order Channel",
          matchWith: ["offline"],
          multiplier: 1.5
        }
      ]
    },
    config: null
  });

  // Helper function to create child object
  const createChildObject = async (salesTargetData, dateFields, categoryList) => {
    const childRuleSet = await transformCategoriesToLabels(salesTargetData?.tabWiseData?.child?.ruleSet, categoryList);
    return {
      id: "",
      storeList: "",
      targetName: salesTargetData?.targetName || "",
      ruleSet: childRuleSet,
      displayContent: filterDisplayContent(salesTargetData?.child?.displayContent),
      targetType: salesTargetData?.targetTypeValue || "",
      awardPointsImmedFlag: false,
      joinCampaignFlag: true,
      VATIncluded: true,
      cepRefId: salesTargetData?.cepRefId ?? null,
      childRuleSet: null,
      roundPointsValue: salesTargetData?.roundPointsValue || "",
      roundUpPointsFlag: salesTargetData?.roundUpPointsFlag ?? false,
      ...dateFields,
      media: transformMediaArray([
        ...(salesTargetData?.displayContentMedia ? [salesTargetData.displayContentMedia] : []),
        ...(salesTargetData?.displayContentMediaOptional ? [salesTargetData.displayContentMediaOptional] : [])
      ]),
      status: salesTargetData?.status,
      ignoreSkuList: salesTargetData?.tabWiseData?.child?.ignoreSkuList ?? false,
      offlineOrdersIncluded: salesTargetData?.offlineOrdersIncluded ?? false,
      pointDisplay: null,
      children: [],
      application: createApplicationObject(),
      parent: null,
      totalTasks: null,
      metadata: createMetadata(salesTargetData, 'child')
    }
  };

  // Helper function to transform categories to labels
  const transformCategoriesToLabels = async (ruleSet, categoryList) => {
    if (ruleSet == null || ruleSet == undefined || !ruleSet.rules?.[0]?.includedCategories) {
      return ruleSet;
    }
    const includedCategoryIds = ruleSet.rules[0].includedCategories;
    const excludedCategoryIds = ruleSet.rules[0].excludedCategories || [];
    const includedCategoriesLabels = categoryList
      .filter(cat => includedCategoryIds.includes(cat.categoryId) || includedCategoryIds.includes(cat.category))
      .map(cat => cat.category);
    const excludedCategoriesLabels = categoryList
      .filter(cat => excludedCategoryIds.includes(cat.categoryId) || excludedCategoryIds.includes(cat.category))
      .map(cat => cat.category);
    const ruleSet1 = { ...ruleSet, rules: [{ ...ruleSet.rules[0], includedCategories: includedCategoriesLabels, excludedCategories: excludedCategoriesLabels }] }
    return ruleSet1;
  }

  // Helper function to create request object for regular targets
  const createReqObj = async (salesTargetData, categoryList) => {
    const ruleSet = await transformCategoriesToLabels(salesTargetData?.tabWiseData?.parent?.ruleSet, categoryList)
    const childRuleSet = await transformCategoriesToLabels(salesTargetData?.childRuleSet, categoryList)
    const isLayeredTarget = salesTargetData?.targetType === TARGET_TYPES.ProductTargetLayered || salesTargetData?.targetType === TARGET_TYPES.MonthlyTargetLayered
    const isMainTargetOrProdTarget = salesTargetData?.targetType === TARGET_TYPES.MainTarget || salesTargetData?.targetType === TARGET_TYPES.ProductTarget

    const result = {
      targetName: salesTargetData?.targetName,
      displayContent: Object.keys(salesTargetData?.displayContent || {})
        .filter(langKey => langKey !== 'child')
        .reduce((acc, langKey) => {
          acc[langKey] = salesTargetData?.displayContent?.[langKey];
          return acc;
        }, {}),
      ignoreSkuList: salesTargetData?.tabWiseData?.parent?.ignoreSkuList,
      targetType: salesTargetData?.targetTypeValue || "",
      excludeTaxFromGoal: salesTargetData?.excludeTaxFromGoal,
      offlineOrdersIncluded: salesTargetData?.offlineOrdersIncluded,
      roundUpPointsFlag: salesTargetData?.roundUpPointsFlag,
      joinCampaignFlag: salesTargetData?.joinCampaignFlag,
      roundPointsValue: salesTargetData?.roundPointsValue || salesTargetData?.roundOffValue || '',
      VATIncluded: !salesTargetData?.excludeTaxFromGoal,
      cepRefId: salesTargetData?.cepRefId,
      displayStartDate: getISOTimeStringForReview(salesTargetData?.displayStartDate, false, true),
      displayEndDate: getISOTimeStringForReview(salesTargetData?.displayEndDate, true, false),
      activeStartDate: getISOTimeStringForReview(salesTargetData?.activeStartDate, false, true),
      activeEndDate: getISOTimeStringForReview(salesTargetData?.activeEndDate, true, false),
      targetDeliveryDate: getISOTimeStringForReview(salesTargetData?.targetDeliveryDate, true, false),
      pointsDeliveryDate: getISOTimeStringForReview(salesTargetData?.pointsDeliveryDate, true, false),
      pointDisplay: isLayeredTarget ? salesTargetData?.pointDisplay : null,
      ruleSet: ruleSet,
      media: transformMediaArray([
        ...(salesTargetData?.displayContentMedia ? [salesTargetData.displayContentMedia] : []),
        ...(salesTargetData?.displayContentMediaOptional ? [salesTargetData.displayContentMediaOptional] : [])
      ]),
      childRuleSet: childRuleSet,
      status: salesTargetData?.status,
      awardPointsImmedFlag: isMainTargetOrProdTarget ? salesTargetData?.awardPointsImmedFlag : null,
      storeList: salesTargetData?.storeList,
      metadata: {
        productFileName: salesTargetData?.selectedType === Constants.UploadedProducts ? salesTargetData?.tabWiseData?.parent?.mediaFile[0] : null,
        isProductIdsUploaded: salesTargetData?.selectedType === Constants.UploadedProducts ? true : false,
        numberLevel: isLayeredTarget ? salesTargetData?.numberLevel : null,
        numberMileStone: isLayeredTarget ? salesTargetData?.numberMileStone : null,
      }
    }
    return result
  }

  // Helper function to create request object for prerequisite targets
  const createPreReqReqObj = async (salesTargetData, categoryList) => {
    const ruleSet = await transformCategoriesToLabels(salesTargetData?.tabWiseData?.parent?.ruleSet, categoryList);
    const dateFields = formatDateFields(salesTargetData);

    const result = {
      storeList: salesTargetData?.storeList || [],
      targetName: salesTargetData?.targetName || "",
      ruleSet,
      targetType: salesTargetData?.targetTypeValue || "",
      awardPointsImmedFlag: false,
      joinCampaignFlag: salesTargetData?.joinCampaignFlag,
      VATIncluded: true,
      cepRefId: salesTargetData?.cepRefId ?? null,
      childRuleSet: null,
      roundPointsValue: salesTargetData?.roundPointsValue || salesTargetData?.roundOffValue || '',
      roundUpPointsFlag: salesTargetData?.roundUpPointsFlag ?? false,
      excludeTaxFromGoal: salesTargetData?.excludeTaxFromGoal ?? false,
      ...dateFields,
      media: transformMediaArray([
        ...(salesTargetData?.displayContentMedia ? [salesTargetData.displayContentMedia] : []),
        ...(salesTargetData?.displayContentMediaOptional ? [salesTargetData.displayContentMediaOptional] : [])
      ]),
      displayContent: filterDisplayContent(salesTargetData?.displayContent, true),
      ignoreSkuList: salesTargetData?.tabWiseData?.parent?.ignoreSkuList ?? false,
      offlineOrdersIncluded: salesTargetData?.offlineOrdersIncluded ?? false,
      pointDisplay: null,
      child: await createChildObject(salesTargetData, dateFields, categoryList),
      application: null,
      parent: null,
      totalTasks: null,
      metadata: createMetadata(salesTargetData, 'parent')
    };

    return result;
  };

  const shouldTransformRuleSetOfSalesTarget = (step) => {
    return step === 'products' || step === 'review';
  }

  const getDisplayContent = (isPrerequisiteTarget, additionalData) => {
    return isPrerequisiteTarget
      ? additionalData.displayPreReqContentFormData
      : additionalData.displayContentFormData;
  }

  const formatDraftNotificationMessage = (hasExistingUuid) => {
    return  hasExistingUuid? 'Updated Sales Target': 'New Sales Target Drafted'
  }

  const handleNameValidation = async (queryParams) => {
   const data = await handleNameValidationViaCallbackFn(validateSalesTargetName,queryParams)
   return data
  }

  const handleDraft = async (formData, salesTarget, isPrerequisiteTarget, step = 'setup', additionalData = {}) => {
    const hasExistingUuid = salesTarget.uuid !== null;
    const data = { ...formData }

    // Format data based on step
    const formattedData = formatDataForStep(data, step)

    // Handle step-specific logic
    switch (step) {
      case 'setup':
        formattedData.numberLevel = formattedData.numberLevel || null
        formattedData.numberMileStone = formattedData.numberMileStone || null
        break
      case 'displayContent':
        formattedData.displayContent = getDisplayContent(isPrerequisiteTarget, additionalData)
        break
      case 'products':
        // Products step uses the same data formatting as other steps
        // The tranformRuleSetOfSalesTarget is be applied after createSalesTargetRequestObject
        break
      case 'review':
        // Add review specific formatting if needed
        break
      default:
        break
    }

    let requestData = createSalesTargetRequestObject(formattedData, isPrerequisiteTarget)
    requestData.uuid = hasExistingUuid ? salesTarget.uuid : getUUID(formattedData?.targetName);
    requestData.status = 'Draft';

    // Apply tranformRuleSetOfSalesTarget for products step
    if (shouldTransformRuleSetOfSalesTarget(step) && additionalData.categoryListData) {
      requestData = tranformRuleSetOfSalesTarget(requestData, additionalData.categoryListData);
    }

    try {
      let response;
      if (hasExistingUuid) {
        response = await updateDraftSalesTargetList({ data: requestData })
      } else {
        response = await addDraftSalesTargetList({ data: requestData })
      }

      if (response.success) {
        dispatch(resetSalesTarget())
        dispatch(setToastNotifications({ show: true, message: formatDraftNotificationMessage(hasExistingUuid), type: 'info' }))
      } else {
        dispatch(setToastNotifications({ show: true, message: 'New Sales Target not Drafted', type: 'error' }))
      }

      return response
    } catch (error) {
      console.error('Error in handleDraft:', error);
      dispatch(setToastNotifications({ show: true, message: 'New Sales Target not Drafted', type: 'error' }))
      throw error;
    }
  }

  const handlePublish = async (salesTargetData, isPrerequisiteTarget, categoryList, navigate, ROUTES) => {
    const hasExistingUuid = salesTargetData.uuid !== null;
    const reqObj = isPrerequisiteTarget ? await createPreReqReqObj(salesTargetData, categoryList) : await createReqObj(salesTargetData, categoryList)
    reqObj['uuid'] = hasExistingUuid ? salesTargetData.uuid : getUUID(reqObj.targetName);
    reqObj.status = 'Published';
    try {
      const response = await addSalesTargetList({ data: reqObj })
      if (Object.keys(response).length > 0) {
        if(response?.success==false) {
          const errMsg = response?.message ||'An unknown error occurred while publishing sales target'
          dispatch(setToastNotifications({show: true, message:  errMsg, type: 'error'}))
        }
        else{
          dispatch(setToastNotifications({ show: true, message: 'New Sales Target published', type: 'success' }))
          dispatch(resetSalesTarget())
          navigate(ROUTES.SALES_TARGETS)
        }
      } else dispatch(setToastNotifications({ show: true, message: 'New Sales Target not published', type: 'error' }))
    } catch (err) {
      const errMsg = 'An unknown error occurred while publishing sales target. Please verify the data and try again.'
      dispatch(setToastNotifications({show: true, message: errMsg, type: 'error'}))
      console.log(err)
    }
  }

  const resultsData = useMemo(() => data || {}, [data])

  const salesTargetsData = useMemo(() => GetFormattedSalesTargetData(resultsData), [resultsData])

  const onTemplateUpdate = async (id, requestData) => {
    try {
      let response = await updateEditSalesTargetTemplate(id, {fileUrl: requestData})
      if (response?.success) {
        showToast('Template Updated Successfully', 'success')
      } else {
        showToast('Template Update Failed', 'success')
      }
    } catch (error) {
      console.error('Error in onTemplateUpdate:', error)

      showToast('Template Update Failed', 'error')
    }
  }
  return {
    salesTargetsColConfigData: SalesTargetsColConfig,
    salesTargetsData,
    pageIndex,
    setPageIndex,
    setSortingObj,
    sortingObj,
    setSearchArry,
    onDownloadBtnClick,
    searchArr,
    isLoading: isLoading || isFetching,
    isNameValid,
    setIsNameValid,
    handleResetAll,
    handleDraft,
    handlePublish,
    handleNameValidation,
    handleValidationModalClose,
    onTemplateUpdate,
    isValidDateRange,
  }
}
